// ABOUTME: SPEC-46 Rev 7 — the in-app payment panel: Stripe Embedded Checkout, mounted here.
// ABOUTME: Chrome borrowed verbatim from WaitlistModal (portal, scrim, safe area, focus trap).
//
// This panel is the whole point of Rev 7. Rev 6 sent the guest to checkout.stripe.com in a second
// tab, so the tab holding the WebSocket could not be told when the payment finished, and the
// concierge went silent. Here the payment happens inside the boutique: the socket never moves,
// so `onComplete` is delivered on the same connection that owns the bag and the voice.
//
// Three things about embedded checkout that are not obvious and are each a real bug if missed:
//
//   1. The mount node must have NO fixed height. Stripe's iframe measures its own content and
//      grows; a container with a height clips the pay button below the fold with nothing to
//      scroll. `checkoutGeometry.MOUNT_MIN_H_PX` is a MINIMUM, never a height.
//   2. Cleanup calls `destroy()`, not `unmount()`. `unmount()` leaves the instance alive, and
//      the next mount throws "you cannot have multiple embedded checkout instances".
//   3. React 19 StrictMode runs effects twice in development. Without the `alive` guard the
//      second run creates a second instance against the same secret and Stripe throws.
//
// Closing the panel before paying is a silent cancel (OQ-4): the bag is untouched, the agent says
// nothing, and the guest can start again. Nothing is "abandoned" from our side.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getStripe } from './stripeLoader';
import {
  PANEL_W_PX, MOUNT_MIN_H_PX, COMPLETE_DWELL_MS, CLOSE_BOX_PX, CLOSE_GLYPH_PX, LOAD_TIMEOUT_MS,
} from './checkoutGeometry';

// Everything the guest can reach with a keyboard, in DOM order (WaitlistModal's list).
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export const COPY = {
  title: 'Secure checkout',
  // Never names the provider — the house's own checkout, from the guest's perspective (D-4).
  dueToday: 'Due today',
  loading: 'Opening secure payment…',
  complete: 'Payment received — thank you.',
  close: 'Close checkout',
  errors: {
    loader_failed: "We couldn't open secure payment. Please check your connection and try again.",
    offline: 'We lost the connection for a moment. Please try again.',
    mount_failed: "We couldn't open secure payment just now. Please try again.",
    expired: 'This checkout has expired. Start again and we will set up a fresh one.',
  },
  retry: 'Try again',
  restart: 'Start again',
};

/** @param {ReturnType<import('./useCheckout').useCheckout>} checkout */
export default function CheckoutOverlay(checkout) {
  const { open, status, error, offer, publishableKey, complete, close, fail, requestCheckout } = checkout;
  const mountRef = useRef(null);
  const panelRef = useRef(null);
  const restoreFocusTo = useRef(null);
  const [mounting, setMounting] = useState(false);

  const clientSecret = offer?.clientSecret || '';
  const showPaymentForm = open && status !== 'complete' && status !== 'error';

  // The effect below must re-run ONLY when the payment session changes — never because the app
  // re-rendered. `complete` and `fail` come from the hook, and `complete` changes identity on
  // every App render (App's `sendMessage` is an inline arrow, and this app re-renders constantly
  // for audio, transcript and animation). Depending on them directly tore down and remounted
  // Stripe mid-payment (external review, 2026-09-15). Refs give the effect the current callbacks
  // without making them part of its identity.
  const completeRef = useRef(complete);
  const failRef = useRef(fail);
  completeRef.current = complete;
  failRef.current = fail;

  // --- mount / destroy the embedded instance -------------------------------------------------
  useEffect(() => {
    if (!showPaymentForm || !clientSecret || !publishableKey) return undefined;
    let alive = true;               // StrictMode double-invoke guard (note 3 above)
    let instance = null;
    setMounting(true);

    // Neither `loadStripe` nor `initEmbeddedCheckout` carries its own timeout, so a promise that
    // never settles left the guest looking at "Opening secure payment…" forever with no way
    // forward but the close button.
    const watchdog = setTimeout(() => {
      if (alive) failRef.current?.('loader_failed');
    }, LOAD_TIMEOUT_MS);

    (async () => {
      try {
        const stripe = await getStripe(publishableKey);
        if (!alive) return;
        if (!stripe) { failRef.current?.('loader_failed'); return; }
        // `initEmbeddedCheckout` is REMOVED from current Stripe.js — calling it throws
        // "stripe.initEmbeddedCheckout() has been removed. Please use
        // stripe.createEmbeddedCheckoutPage() instead." (observed live on dev, 2026-09-16).
        // Both names are still present as functions, so feature-detection has to be on the
        // NEWER one: testing for `initEmbeddedCheckout` finds it and then throws.
        //
        // The two take different options: the current API pulls the secret through a callback
        // rather than taking it up front, which is also what lets Stripe start loading before
        // the secret resolves.
        const onComplete = () => completeRef.current?.();
        instance = typeof stripe.createEmbeddedCheckoutPage === 'function'
          ? await stripe.createEmbeddedCheckoutPage({
              fetchClientSecret: async () => clientSecret,
              onComplete,
            })
          : await stripe.initEmbeddedCheckout({ clientSecret, onComplete });
        if (!alive) { try { instance.destroy?.(); } catch { /* already gone */ } return; }
        if (!mountRef.current) {
          try { instance.destroy?.(); } catch { /* already gone */ }
          failRef.current?.('mount_failed');
          return;
        }
        instance.mount(mountRef.current);
        clearTimeout(watchdog);
        setMounting(false);
      } catch (err) {
        if (!alive) return;
        // Stripe rejects a secret whose session has completed or expired (they live ~24h). That
        // is recoverable by asking for a new session, so it gets its own message rather than the
        // generic failure.
        const message = String(err?.message || '');
        failRef.current?.(/expire/i.test(message) ? 'expired' : 'mount_failed');
      }
    })();

    return () => {
      alive = false;
      clearTimeout(watchdog);
      try { instance?.destroy?.(); } catch { /* already gone */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are held in refs above,
    // deliberately: this effect's identity is the payment session, not the render.
  }, [showPaymentForm, clientSecret, publishableKey]);

  // --- the panel closes itself once the payment lands ----------------------------------------
  useEffect(() => {
    if (status !== 'complete') return undefined;
    const timer = setTimeout(() => close?.(), COMPLETE_DWELL_MS);
    return () => clearTimeout(timer);
  }, [status, close]);

  // --- Escape, and focus restored where the guest left it ------------------------------------
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusTo.current = document.activeElement;
    panelRef.current?.focus?.();
    return () => {
      const target = restoreFocusTo.current;
      if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
    };
  }, [open]);

  // `aria-modal` PROMISES focus cannot leave; nothing was enforcing it, so a keyboard or
  // screen-reader user could Tab straight into the reel behind the scrim. WaitlistModal carries
  // the same guard for the same reason (VER-89). Note the honest limit: once focus is inside
  // Stripe's cross-origin iframe neither this nor the Escape listener can see the keystrokes —
  // which is exactly why the close button is always rendered and never scrolls away.
  const onPanelKeyDown = (e) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    // JSDOM does not move focus on Tab, so both ends are driven explicitly rather than only the
    // edge being intercepted — which also makes the behaviour identical in a browser.
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  if (!open) return null;

  const errorMessage = error ? (COPY.errors[error] || COPY.errors.mount_failed) : '';
  const retryLabel = error === 'expired' ? COPY.restart : COPY.retry;
  const onRetry = () => { close?.(); requestCheckout?.(); };

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label={COPY.title}
      onClick={() => close?.()}
      style={{
        paddingTop: 'calc(1rem + var(--reel-safe-t))',
        paddingBottom: 'calc(1rem + var(--reel-safe-b))',
        paddingLeft: 'calc(1rem + var(--reel-safe-l))',
        paddingRight: 'calc(1rem + var(--reel-safe-r))',
      }}
      className="fixed inset-0 z-[205] bg-black/60 backdrop-blur-md flex items-center justify-center pointer-events-auto"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-reel-chrome-panel backdrop-blur-reel-chrome border border-white/10 shadow-2xl flex flex-col"
        style={{
          // `100%` is the backdrop's content box, which already has the 16px + safe-area
          // padding subtracted — `92vw` guessed at that and did not account for an inset.
          width: `min(${PANEL_W_PX}px, 100%)`,
          maxHeight: '88%',
          borderRadius: '18px',
          padding: '24px',
        }}
      >
        {/* The close affordance sits in a flex ROW with the heading, not hung off the panel's
            corner and not absolutely positioned over it. Two reasons, both learned from a render:
              - WaitlistModal offsets its X outward by `min(box, (100vw - 100%) / 2)`, which on a
                390px phone resolves to ~15px and lands the button's right edge exactly on the
                viewport edge — fine in a headless plate, clipped by any right safe-area inset.
              - Absolutely positioning it INSIDE instead put its box on top of the full-width
                heading's box. The glyphs never touched, but `scripts/plates_shoot.js`'s overlap
                probe compares boxes and was right to flag it; a row makes the geometry honest
                rather than teaching the probe to cry wolf here.
            The row lives OUTSIDE the scroll container so the close never scrolls away. */}
        <div className="flex items-start justify-between gap-3" style={{ marginBottom: '8px' }}>
          <h2
            className="font-reel-heading font-extrabold text-reel-chrome-textPrimary"
            style={{ fontSize: '20px' }}
          >
            {COPY.title}
          </h2>
          <button
            type="button" onClick={close} aria-label={COPY.close}
            className="relative flex-none flex items-center justify-center rounded-full bg-reel-chrome-iconChip text-reel-chrome-textPrimary hover:opacity-70 transition-opacity reel-touch-target"
            style={{ width: `${CLOSE_BOX_PX}px`, height: `${CLOSE_BOX_PX}px` }}
          >
            <X aria-hidden="true" style={{ width: CLOSE_GLYPH_PX, height: CLOSE_GLYPH_PX }} />
          </button>
        </div>

        {/* Scrolling lives on this inner container, never on the panel — WaitlistModal.js
            records why (overflow-y forces overflow-x to auto, VER-89). */}
        <div className="overflow-y-auto overscroll-contain min-h-0">
          {offer?.dueTodayDisplay ? (
            <p
              className="font-reel-body uppercase tracking-widest text-reel-chrome-textDim"
              style={{ fontSize: '12px', marginBottom: '20px' }}
            >
              {`${COPY.dueToday} ${offer.dueTodayDisplay}`}
            </p>
          ) : null}

          {status === 'complete' ? (
            <p
              role="status"
              className="font-reel-body text-reel-chrome-textDim leading-relaxed"
              style={{ fontSize: '15px' }}
            >
              {COPY.complete}
            </p>
          ) : status === 'error' ? (
            <>
              <p
                role="alert"
                className="font-reel-body text-reel-chrome-textPrimary leading-relaxed"
                style={{ fontSize: '15px', marginBottom: '16px' }}
              >
                {errorMessage}
              </p>
              <button
                type="button" onClick={onRetry}
                className="w-full uppercase bg-reel-cta-primaryBg text-reel-chrome-textInk font-reel-body flex items-center justify-center text-center leading-tight hover:opacity-90 transition-opacity"
                style={{ minHeight: '48px', borderRadius: '10px', fontSize: '13px' }}
              >
                {retryLabel}
              </button>
            </>
          ) : (
            <>
              {mounting && (
                <p
                  className="font-reel-body text-reel-chrome-textDim"
                  style={{ fontSize: '14px', marginBottom: '12px' }}
                >
                  {COPY.loading}
                </p>
              )}
              {/* No height here, on purpose — see note 1 at the top of this file. */}
              <div ref={mountRef} data-testid="checkout-mount" style={{ minHeight: `${MOUNT_MIN_H_PX}px` }} />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
